import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calculator, BookOpen, List, Wine, Droplets, Martini, Snowflake, AlertTriangle } from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cual es el food cost ideal en un bar de cocteles?',
    a: 'El food cost objetivo en cocteleria es del 15-22%, significativamente menor que en cocina (28-35%). Los cocteles clasicos con ingredientes economicos (mojito, daikiri) pueden tener food cost del 12-18%, mientras que los cocteles premium con destilados caros (gin tonic con Hendrick\'s, Old Fashioned con bourbon premium) se situan entre el 20-28%. Un bar de cocteles bien gestionado deberia mantener un food cost global de bebidas por debajo del 22%.',
  },
  {
    q: '¿Como calculo cuantos cocteles salen de una botella de destilado?',
    a: 'Divide el contenido de la botella (normalmente 70 cl o 100 cl) entre la medida estandar del coctel. Por ejemplo, una botella de gin de 70 cl con servicio de 5 cl por coctel da 14 cocteles. Con una botella de ron de 70 cl a 5 cl por coctel, salen 14 mojitos. El numero real sera un 5-10% menor por derrames y over-pouring si no usas jigger o dosificador.',
  },
  {
    q: '¿Debo incluir el hielo en el escandallo del coctel?',
    a: 'Si, siempre. Aunque el coste unitario del hielo parece insignificante (0,03-0,10 euros por coctel), en un bar que sirve 80-150 cocteles diarios supone entre 72 y 450 euros al mes. El coste depende de si produces hielo con maquina propia (mas barato a largo plazo, 0,03-0,05 euros/coctel) o si compras hielo en bolsas (0,06-0,10 euros/coctel). El hielo artesanal de bloque para cocteles premium puede costar hasta 0,20-0,30 euros por pieza.',
  },
  {
    q: '¿Como controlo el over-pouring en cocteleria?',
    a: 'El over-pouring es la principal causa de merma invisible en bares de cocteles. Para controlarlo: 1) Obliga al uso de jigger en todas las preparaciones — un bartender profesional nunca sirve sin medir. 2) Instala dosificadores en las botellas de servicio rapido. 3) Haz inventarios semanales comparando consumo teorico (segun ventas) con consumo real (segun stock). 4) Forma al equipo: un camarero que sirve 6 cl en vez de 5 cl esta regalando un 20% del producto.',
  },
  {
    q: '¿Que merma tienen los zumos frescos para cocteles?',
    a: 'Los zumos frescos exprimidos en el momento tienen merma alta: lima y limon un 40-50% (mucha piel y semillas), naranja un 35-40%, pomelo un 35%. Ademas, los zumos frescos se oxidan en 4-8 horas y pierden sabor. La solucion profesional es preparar zumos fresh-press al inicio del servicio (mise en place) y calcular la cantidad justa para no desperdiciar. Algunos bares usan zumos pasteurizados de calidad para reducir merma, aunque el sabor no es comparable.',
  },
  {
    q: '¿Es mas rentable un bar de cocteles que un restaurante?',
    a: 'En terminos de food cost, si. Un bar de cocteles tiene food cost del 15-22% mientras que un restaurante tiene 28-35%. Sin embargo, el restaurante compensa con ticket medio mas alto, mayor rotacion de mesas y combinacion de comida + bebida. El bar de cocteles necesita menos espacio de cocina, menos personal de cocina y menos equipamiento, pero requiere bartenders cualificados (salarios mas altos) y una ubicacion premium. La combinacion ideal es un restaurante con una carta de cocteles bien escandallada.',
  },
  {
    q: '¿Como escandallo un coctel de autor con ingredientes caseros?',
    a: 'Para cocteles de autor con siropes caseros, infusiones o ingredientes elaborados, necesitas hacer un escandallo en cascada: primero escandalla la elaboracion base (ej: sirope de jengibre — coste del jengibre + azucar + agua + tiempo de coccion) y luego usa ese coste calculado como ingrediente del coctel. PLEKO permite crear sub-recetas que se integran automaticamente en el coste del coctel final.',
  },
  {
    q: '¿Cuanto cuesta el garnish y decoracion de un coctel?',
    a: 'El garnish parece insignificante pero puede representar entre un 3% y un 8% del coste total del coctel. Dos rodajas de pepino cuestan 0,05 euros, pero una twist de piel de naranja cuesta 0,08-0,12 euros, una ramita de romero fresco 0,10-0,15 euros, y un deshidratado artesanal puede costar 0,30-0,50 euros. En un bar que sirve 100 cocteles al dia, el garnish puede sumar 150-500 euros al mes. Incluyelo siempre en el escandallo.',
  },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Escandallo de Cocteleria — Calcula el Coste Real de Cada Coctel de tu Carta',
  description: 'Guia completa para calcular el escandallo de cocteles: Gin Tonic, Mojito, Espresso Martini y Margarita con tablas detalladas, mermas, hielo y food cost objetivo 15-22%.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' } },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    { '@type': 'ListItem', position: 3, name: 'Escandallo de Cocteleria', item: 'https://pleko.app/escandallo-cocteleria' },
  ],
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que-cocteleria', label: 'Por que la cocteleria necesita escandallos especificos' },
  { id: 'componentes-coste', label: 'Componentes del coste de un coctel' },
  { id: 'ejemplo-gin-tonic', label: 'Ejemplo: Gin Tonic Premium' },
  { id: 'ejemplo-mojito', label: 'Ejemplo: Mojito Clasico' },
  { id: 'ejemplo-espresso-martini', label: 'Ejemplo: Espresso Martini' },
  { id: 'ejemplo-margarita', label: 'Ejemplo: Margarita Clasica' },
  { id: 'mermas-cocteleria', label: 'Mermas especificas en cocteleria' },
  { id: 'coste-hielo', label: 'El coste oculto del hielo' },
  { id: 'food-cost-objetivo', label: 'Food cost objetivo en cocteleria (15-22%)' },
  { id: 'fijar-pvp', label: 'Como fijar el PVP de cocteles con margen rentable' },
  { id: 'software-vs-excel', label: 'Software vs Excel para bares de cocteles' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function EscandalloCocteleriaPage() {
  return (
    <>
      <Helmet>
        <title>Escandallo de Cocteleria — Calcula el Coste Real de Cada Coctel | PLEKO</title>
        <meta
          name="description"
          content="Calcula el escandallo de cocteles con 4 ejemplos reales: Gin Tonic, Mojito, Espresso Martini y Margarita. Tablas con ingredientes, mermas, hielo y food cost objetivo 15-22% para bares de cocteles."
        />
        <meta
          name="keywords"
          content="escandallo cocteleria, coste coctel, food cost bar, escandallo bar de cocteles, escandallo gin tonic, escandallo mojito, coste coctel bar, food cost cocteleria, escandallo bebidas cocteleria, rentabilidad bar cocteles, margen coctel, escandallo espresso martini, escandallo margarita"
        />
        <link rel="canonical" href="https://pleko.app/escandallo-cocteleria" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/escandallo-cocteleria" />
        <meta property="og:title" content="Escandallo de Cocteleria — Calcula el Coste Real de Cada Coctel" />
        <meta property="og:description" content="Calcula el escandallo de cocteles con 4 ejemplos reales. Tablas con ingredientes, mermas, hielo y food cost objetivo 15-22% para bares de cocteles." />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Escandallo de Cocteleria — Coste Real de Cada Coctel | PLEKO" />
        <meta name="twitter:description" content="4 escandallos de cocteles con tablas detalladas: Gin Tonic, Mojito, Espresso Martini, Margarita. Food cost objetivo 15-22%." />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-amber-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-6">
                <Martini className="w-4 h-4" />
                Guia especializada para bares de cocteles y cocteleria — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Escandallo de <span className="text-amber-500">Cocteleria</span>: Calcula el Coste Real de Cada Coctel de tu Carta
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Los cocteles tienen el <strong>mejor margen de toda la hosteleria</strong> (food cost 15-22%), pero solo si controlas cada centimo. Aprende a escandallar tus cocteles con ejemplos reales, mermas especificas y precios de distribuidor actualizados.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#ejemplo-gin-tonic"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-200"
                >
                  Ver escandallos de cocteles
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Calculadora de costes
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-amber-600 hover:text-amber-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">

            {/* Section 1: Por que la cocteleria necesita escandallos especificos */}
            <h2 id="por-que-cocteleria" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">¿Por que la cocteleria necesita escandallos especificos?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>escandallo de cocteleria</strong> comparte la base del <Link to="/escandallo" className="text-amber-600 hover:underline">escandallo clasico</Link>, pero tiene particularidades que lo hacen unico. Un coctel tiene entre 3 y 6 ingredientes, pero cada uno de ellos esconde trampas de coste que no existen en la cocina: <strong>destilados caros que se pierden por over-pouring, ingredientes perecederos que caducan en horas, y un coste de hielo que nadie contabiliza</strong>.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La cocteleria es, paradojicamente, el area con <strong>mejor margen y peor control</strong> de la hosteleria. Un bar que no escandalla sus cocteles puede estar perdiendo entre un 10% y un 25% de su margen sin saberlo. Un gin de 35 euros/botella servido a 6 cl en vez de 5 cl son 5 euros de regalo por cada 14 gin tonics — 150 euros al mes solo con ese destilado. Multiplicalo por 15-20 referencias y el problema es enorme.
            </p>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-8">
              <p className="text-amber-800 font-medium text-center text-lg mb-2">
                ¿Por que escandallar cocteles es diferente a escandallar platos?
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mt-4">
                <div className="text-center">
                  <Wine className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-800">Destilados caros</p>
                  <p className="text-xs text-slate-600 mt-1">Botellas de 20-50 euros donde cada mililitro cuenta</p>
                </div>
                <div className="text-center">
                  <Droplets className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-800">Ingredientes perecederos</p>
                  <p className="text-xs text-slate-600 mt-1">Zumos frescos, hierbas y garnish con vida util de horas</p>
                </div>
                <div className="text-center">
                  <Snowflake className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-800">Hielo como ingrediente</p>
                  <p className="text-xs text-slate-600 mt-1">Coste oculto de 0,03-0,30 euros por coctel</p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-800 mb-4">5 razones para escandallar cada coctel de tu carta</h3>
            <ul className="space-y-3 mb-8">
              {[
                'Los destilados premium (gin, whisky, mezcal) pueden costar 30-60 euros por botella — cada mililitro de over-pouring es dinero perdido',
                'Los zumos frescos (lima, limon, naranja) tienen mermas del 35-50% que multiplican el coste real',
                'El garnish parece barato pero puede sumar 150-500 euros al mes en un bar activo',
                'El hielo es un ingrediente invisible que representa 90-450 euros mensuales segun volumen',
                'La diferencia entre un bar que escandalla y uno que no puede ser de 2.000-5.000 euros al mes en margen perdido',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {/* Section 2: Componentes del coste de un coctel */}
            <h2 id="componentes-coste" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Componentes del Coste de un Coctel</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Todo coctel tiene 5 componentes de coste que debes incluir en el escandallo. Omitir cualquiera de ellos es subestimar el coste real y, por tanto, sobreestimar tu margen. Este desglose aplica a cualquier coctel, desde un simple gin tonic hasta un coctel de autor con 8 ingredientes:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-800 text-white">
                    <th className="p-3 text-left font-medium">Componente</th>
                    <th className="p-3 text-center font-medium">% del coste total</th>
                    <th className="p-3 text-left font-medium">Ejemplos</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Destilado base', '50-70%', 'Gin, ron, vodka, tequila, whisky, mezcal', 'bg-amber-50'],
                    ['Licores y modificadores', '10-25%', 'Triple sec, Campari, vermut, licor de cafe, amaretto', 'bg-white'],
                    ['Zumos frescos y mixers', '5-20%', 'Lima, limon, naranja, tonica, soda, ginger beer', 'bg-amber-50'],
                    ['Garnish y decoracion', '3-8%', 'Rodaja de lima, twist de naranja, ramita de menta, aceituna', 'bg-white'],
                    ['Hielo', '2-5%', 'Hielo en cubos, hielo picado, bloque artesanal', 'bg-amber-50'],
                  ].map(([componente, porcentaje, ejemplos, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{componente}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-amber-700">{porcentaje}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{ejemplos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              El <strong>destilado base siempre es el ingrediente mas caro</strong>, representando entre el 50% y el 70% del coste total del coctel. Por eso, la eleccion de la marca del destilado es la decision que mas impacto tiene en tu food cost. Un gin a 15 euros/botella vs uno a 35 euros/botella cambia completamente el escandallo. Para una vision mas amplia de todas las bebidas, consulta nuestra <Link to="/escandallo-bebidas" className="text-amber-600 hover:underline">guia completa de escandallos de bebidas</Link>.
            </p>

            {/* Section 3: Ejemplo Gin Tonic Premium */}
            <h2 id="ejemplo-gin-tonic" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Martini className="w-7 h-7 text-amber-500" /> Ejemplo Completo: Escandallo de Gin Tonic Premium</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El gin tonic es el <strong>coctel mas vendido en Espana</strong> y la referencia obligada de cualquier barra. Aqui lo escandallamos con producto premium — ginebra Hendrick's y tonica Fever-Tree — para que veas el desglose real:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Gin Hendrick's", '5', 'cl', '35,00 euros/70cl = 50,00 euros/L', '0%', '2,50 euros'],
                    ['Tonica Fever-Tree', '1', 'ud (20cl)', '1,20 euros/ud', '0%', '1,20 euros'],
                    ['Pepino fresco (2 rodajas)', '10', 'g', '1,80 euros/kg', '15%', '0,02 euros'],
                    ['Pimienta rosa (4 granos)', '1', 'g', '45,00 euros/kg', '0%', '0,05 euros'],
                    ['Hielo (cubos grandes)', '200', 'g', '0,40 euros/kg', '0%', '0,08 euros'],
                  ].map(([ing, qty, unidad, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{unidad}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-500 text-sm">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-amber-600 text-lg">3,85 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-amber-600">3,85 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP recomendado</p>
                <p className="text-2xl font-bold text-slate-800">13,00 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">29,6%</p>
                <p className="text-xs text-yellow-600 font-medium">Aceptable — margen mejorable</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                Con un PVP de 13 euros, el food cost es del 29,6% — aceptable pero mejorable para cocteleria. <strong>Opcion A:</strong> sube a 14 euros (food cost 27,5%). <strong>Opcion B:</strong> usa una ginebra premium pero mas economica (25 euros/70cl en vez de 35 euros), bajando el coste a 2,85 euros (food cost 21,9% a 13 euros). <strong>Opcion C:</strong> usa una tonica de calidad a 0,85 euros en vez de Fever-Tree, ahorrando 0,35 euros por servicio.
              </p>
            </div>

            {/* Section 4: Ejemplo Mojito */}
            <h2 id="ejemplo-mojito" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Droplets className="w-7 h-7 text-green-500" /> Ejemplo: Escandallo de Mojito Clasico</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El mojito es el <strong>coctel mas rentable de la cocteleria clasica</strong>. Sus ingredientes son economicos (ron blanco, lima, hierbabuena, azucar) y el valor percibido por el cliente es alto. Es el coctel que todo bar deberia tener en su carta por su margen excepcional:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-green-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Ron Havana Club 3 anos', '5', 'cl', '18,00 euros/L (12,60 euros/70cl)', '0%', '0,90 euros'],
                    ['Lima fresca (media)', '40', 'g', '3,80 euros/kg', '45%', '0,28 euros'],
                    ['Hierbabuena (6-8 hojas)', '5', 'g', '20,00 euros/kg', '10%', '0,11 euros'],
                    ['Azucar blanco', '15', 'g', '1,20 euros/kg', '0%', '0,02 euros'],
                    ['Soda / agua con gas', '10', 'cl', '0,80 euros/L', '0%', '0,08 euros'],
                    ['Hielo picado', '200', 'g', '0,40 euros/kg', '0%', '0,08 euros'],
                  ].map(([ing, qty, unidad, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{unidad}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-500 text-sm">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-green-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-green-600 text-lg">1,47 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-green-600">1,47 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP recomendado</p>
                <p className="text-2xl font-bold text-slate-800">9,50 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">15,5%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Con un food cost del 15,5%, el mojito genera <strong>8,03 euros de margen bruto por unidad</strong>. Si vendes 25 mojitos al dia en temporada alta, son 200 euros diarios de margen solo con este coctel. El secreto: la lima, la hierbabuena y el azucar cuestan centimos, y el ron blanco es uno de los destilados mas economicos. Es el coctel perfecto para happy hours — incluso a 7 euros, el food cost seria del 21%, aun en rango excelente.
            </p>

            {/* Section 5: Ejemplo Espresso Martini */}
            <h2 id="ejemplo-espresso-martini" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Martini className="w-7 h-7 text-slate-800" /> Ejemplo: Escandallo de Espresso Martini</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El Espresso Martini es el <strong>coctel de moda de los ultimos anos</strong> — la combinacion de cafeina y alcohol lo ha convertido en imprescindible en cualquier carta de cocteleria moderna. Tiene un food cost medio-bajo y un valor percibido muy alto:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Vodka Absolut', '4', 'cl', '16,00 euros/L (11,20 euros/70cl)', '0%', '0,64 euros'],
                    ['Licor de cafe Kahlua', '2', 'cl', '20,00 euros/L (14,00 euros/70cl)', '0%', '0,40 euros'],
                    ['Espresso recien hecho', '3', 'cl', '14,00 euros/kg cafe (7g = 1 espresso)', '2%', '0,10 euros'],
                    ['Sirope de azucar', '1,5', 'cl', '3,00 euros/L (casero)', '0%', '0,05 euros'],
                    ['Granos de cafe (3 uds garnish)', '1', 'g', '14,00 euros/kg', '0%', '0,01 euros'],
                  ].map(([ing, qty, unidad, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{unidad}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-500 text-sm">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-100">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-slate-800 text-lg">1,20 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-slate-800">1,20 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP recomendado</p>
                <p className="text-2xl font-bold text-slate-800">11,00 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">10,9%</p>
                <p className="text-xs text-green-600 font-medium">Excepcional</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                El Espresso Martini tiene un food cost excepcional del 10,9% porque sus ingredientes son baratos (vodka estandar, cafe, sirope casero) pero el valor percibido es altisimo. Es el coctel <strong>con mejor ratio coste/margen</strong> de toda la carta. Si usas un vodka premium (Grey Goose a 38 euros/L), el coste sube a 1,72 euros (food cost 15,6%), aun excelente. Incluyelo siempre en tu carta.
              </p>
            </div>

            {/* Section 6: Ejemplo Margarita */}
            <h2 id="ejemplo-margarita" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Wine className="w-7 h-7 text-lime-500" /> Ejemplo: Escandallo de Margarita Clasica</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              La Margarita es un <strong>clasico universal</strong> con una relacion calidad-precio excelente. Tequila reposado, triple sec y lima fresca — tres ingredientes que producen un coctel con margen muy saludable:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-lime-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Tequila reposado Jose Cuervo', '5', 'cl', '22,00 euros/L (15,40 euros/70cl)', '0%', '1,10 euros'],
                    ['Triple Sec (Cointreau)', '2,5', 'cl', '28,00 euros/L (19,60 euros/70cl)', '0%', '0,70 euros'],
                    ['Zumo de lima fresco', '3', 'cl', '3,80 euros/kg limas', '45%', '0,21 euros'],
                    ['Sirope de agave', '1', 'cl', '8,00 euros/L', '0%', '0,08 euros'],
                    ['Sal (borde copa)', '2', 'g', '0,50 euros/kg', '0%', '0,00 euros'],
                    ['Hielo', '150', 'g', '0,40 euros/kg', '0%', '0,06 euros'],
                    ['Rodaja de lima (garnish)', '8', 'g', '3,80 euros/kg', '10%', '0,03 euros'],
                  ].map(([ing, qty, unidad, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{unidad}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-500 text-sm">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-lime-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-lime-700 text-lg">2,18 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-lime-50 border border-lime-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-lime-700">2,18 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP recomendado</p>
                <p className="text-2xl font-bold text-slate-800">10,50 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">20,8%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              La Margarita tiene un food cost del 20,8%, dentro del rango ideal para cocteleria. El Cointreau es el ingrediente que mas pesa despues del tequila — si usas un triple sec mas economico (12 euros/L en vez de 28 euros/L), el coste baja a 1,78 euros (food cost 17%). Sin embargo, la calidad del triple sec se nota mucho en este coctel, asi que vale la pena mantener Cointreau si tu posicionamiento es premium.
            </p>

            {/* Section 7: Mermas en cocteleria */}
            <h2 id="mermas-cocteleria" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Mermas Especificas en Cocteleria</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las mermas en cocteleria son muy diferentes a las de la <Link to="/mermas-cocina" className="text-amber-600 hover:underline">cocina</Link>. No hay coccion ni limpieza de piezas grandes, pero hay perdidas especificas que pueden representar entre un 8% y un 20% del coste total si no se controlan:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de merma</th>
                    <th className="p-3 text-center font-medium">Impacto</th>
                    <th className="p-3 text-left font-medium">Causa y solucion</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Derrames y over-pouring', '5-15%', 'Servir sin jigger o dosificador. Solucion: jigger obligatorio + dosificadores en botellas de speed rack'],
                    ['Zumos frescos oxidados', '10-25%', 'Exprimir demasiado zumo que no se usa en el servicio. Solucion: preparar mise en place justa, conservar en frio con film'],
                    ['Garnish desperdiciado', '15-30%', 'Cortar demasiada fruta, hierbas que se marchitan. Solucion: preparar garnish cada servicio, no acumular'],
                    ['Botellas abiertas sin vender', '2-5%', 'Licores y vermuts que se oxidan (especialmente vermut, Campari abierto). Solucion: rotar existencias, almacenar en frio'],
                    ['Hielo derretido', '5-10%', 'Hielo que se derrite en la cubeta durante el servicio. Solucion: reponer con frecuencia, mantener cubetas aisladas'],
                    ['Cocteles devueltos', '1-3%', 'Error de preparacion o cliente insatisfecho. Solucion: formacion del equipo, recetas estandarizadas'],
                    ['Bebidas de cortesia (staff)', '2-5%', 'Invitaciones no registradas al equipo. Solucion: registrar toda invitacion en el TPV'],
                  ].map(([tipo, impacto, causa], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-red-600">{impacto}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{causa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800 mb-1">El over-pouring: el enemigo invisible</p>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Un bartender que sirve 6 cl de destilado en vez de 5 cl esta regalando un <strong>20% del producto</strong>. En una botella de gin a 35 euros, eso son 5 euros de perdida por cada 14 gin tonics. Si tu bar vende 30 gin tonics al dia sin jigger, puedes estar perdiendo <strong>320 euros al mes solo en gin</strong>. Multiplicalo por todos los destilados y la cifra es alarmante. La solucion es simple: <strong>jigger obligatorio, siempre</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 8: Coste oculto del hielo */}
            <h2 id="coste-hielo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">El Coste Oculto del Hielo en Cocteleria</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El hielo es el ingrediente que <strong>nadie incluye en el escandallo</strong> y que puede representar entre 100 y 600 euros mensuales en un bar de cocteles activo. Hay dos modelos: produccion propia con maquina de hielo, o compra de hielo en bolsas o bloques artesanales.
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de hielo</th>
                    <th className="p-3 text-right font-medium">Coste/kg</th>
                    <th className="p-3 text-right font-medium">Coste/coctel (200g)</th>
                    <th className="p-3 text-left font-medium">Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Maquina propia (cubitos)', '0,30-0,50 euros', '0,06-0,10 euros', 'Incluye agua, electricidad y amortizacion. Mas economico a largo plazo', 'bg-green-50'],
                    ['Bolsas compradas (cubitos)', '0,50-0,80 euros', '0,10-0,16 euros', 'Sin inversion inicial. Ideal para bares con poco volumen', 'bg-white'],
                    ['Hielo artesanal (bloques transparentes)', '2,00-4,00 euros', '0,20-0,40 euros', 'Para cocteles premium (Old Fashioned, Negroni). Se corta en cubos grandes', 'bg-amber-50'],
                    ['Hielo seco (efecto humo)', '5,00-8,00 euros', '0,25-0,40 euros', 'Solo para cocteles espectaculo. Uso puntual, no estandar', 'bg-white'],
                  ].map(([tipo, costeKg, costeCoctel, obs, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{costeKg}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-blue-700">{costeCoctel}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{obs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Un bar que sirve 100 cocteles al dia con maquina propia gasta unos <strong>8 euros diarios en hielo (240 euros/mes)</strong>. Con hielo comprado en bolsas, sube a <strong>13 euros diarios (390 euros/mes)</strong>. Si tu bar usa hielo artesanal en cocteles premium, el coste puede triplicarse. La decision entre produccion propia y compra depende del volumen: por encima de 50 cocteles/dia, la maquina propia se amortiza en 6-12 meses.
            </p>

            {/* Section 9: Food cost objetivo en cocteleria */}
            <h2 id="food-cost-objetivo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Food Cost Objetivo en Cocteleria: 15-22%</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El food cost en cocteleria es <strong>significativamente menor que en cocina</strong>. Mientras un restaurante trabaja con food cost del 28-35%, un bar de cocteles bien gestionado deberia mantener un food cost global del 15-22%. Esta es la gran ventaja de la cocteleria: <strong>el margen bruto por unidad es excepcional</strong>.
            </p>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-6">
              <p className="text-amber-800 font-medium text-center text-lg mb-2">
                Semaforo de food cost en cocteleria
              </p>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-2xl font-bold text-green-600">{'<'} 18%</p>
                  <p className="text-sm text-slate-600 mt-1">Excelente — Maximizas margen</p>
                </div>
                <div className="text-center bg-white rounded-lg p-3 border border-yellow-200">
                  <p className="text-2xl font-bold text-yellow-600">18-22%</p>
                  <p className="text-sm text-slate-600 mt-1">Bien — Rango objetivo</p>
                </div>
                <div className="text-center bg-white rounded-lg p-3 border border-red-200">
                  <p className="text-2xl font-bold text-red-600">{'>'} 25%</p>
                  <p className="text-sm text-slate-600 mt-1">Alerta — Revisar precios o mermas</p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de coctel</th>
                    <th className="p-3 text-center font-medium">Food cost objetivo</th>
                    <th className="p-3 text-center font-medium">PVP tipico</th>
                    <th className="p-3 text-center font-medium">Margen bruto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Cocteles clasicos (mojito, daikiri, margarita)', '12-18%', '9-11 euros', '7,50-9,50 euros', 'bg-green-50'],
                    ['Cocteles premium (gin tonic premium, whisky sour)', '18-25%', '12-15 euros', '9-12 euros', 'bg-yellow-50'],
                    ['Cocteles de autor (con elaboraciones propias)', '18-28%', '13-18 euros', '10-14 euros', 'bg-yellow-50'],
                    ['Cocteles sin alcohol (mocktails)', '8-15%', '7-9 euros', '6-8 euros', 'bg-green-50'],
                    ['Shots y chupitos', '10-18%', '3-5 euros', '2,50-4,50 euros', 'bg-green-50'],
                  ].map(([tipo, fc, pvp, margen, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-amber-700">{fc}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{pvp}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-green-700 font-medium">{margen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Observa como los <strong>mocktails (cocteles sin alcohol)</strong> tienen el food cost mas bajo (8-15%) porque no usan destilados. Son una tendencia creciente y una excelente forma de mejorar el food cost global de la barra. Incluir 3-4 mocktails en tu carta no solo atrae a un publico nuevo, sino que mejora tu margen medio. Para mas informacion sobre food cost en hosteleria, consulta nuestra <Link to="/food-cost" className="text-amber-600 hover:underline">guia completa de food cost</Link>.
            </p>

            {/* Section 10: Como fijar el PVP */}
            <h2 id="fijar-pvp" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Como Fijar el PVP de Cocteles con Margen Rentable</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La formula clasica para fijar el PVP de un coctel es sencilla: <strong>PVP = Coste total / Food cost objetivo</strong>. Pero en cocteleria, hay factores adicionales que debes considerar:
            </p>

            <div className="space-y-6 mb-8">
              <div className="border-l-4 border-amber-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. Formula base: coste / food cost objetivo</h3>
                <p className="text-slate-700 leading-relaxed">
                  Si tu mojito cuesta 1,47 euros y quieres un food cost del 18%, el PVP minimo es: 1,47 / 0,18 = <strong>8,17 euros</strong>. Redondeando, 8,50 o 9,00 euros. Si quieres un food cost del 15%, el PVP sube a 9,80 euros — redondeas a 10 euros. Siempre redondea al alza: el cliente no percibe la diferencia entre 9,50 y 10 euros, pero tu margen si.
                </p>
              </div>

              <div className="border-l-4 border-amber-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Analiza la competencia en tu zona</h3>
                <p className="text-slate-700 leading-relaxed">
                  El PVP no depende solo del coste — depende del mercado. Un mojito a 12 euros en el centro de Barcelona es normal, pero en un pueblo de 5.000 habitantes seria excesivo. Investiga los precios de 3-5 bares de tu zona con posicionamiento similar y situa tu PVP en el mismo rango. Si tu producto es claramente superior (mejor destilado, mejor ejecucion), puedes estar un 10-15% por encima.
                </p>
              </div>

              <div className="border-l-4 border-amber-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. Aplica ingenieria de carta a la cocteleria</h3>
                <p className="text-slate-700 leading-relaxed">
                  Disena tu carta de cocteles con un mix estrategico: <strong>60% cocteles de alto margen</strong> (mojito, daikiri, espresso martini) + <strong>40% cocteles premium que atraigan</strong> (gin tonics premium, whisky sours con bourbon top). Los cocteles de alto margen subsidian los premium, y los premium dan imagen de calidad al local. <Link to="/ingenieria-de-menu" className="text-amber-600 hover:underline">Aprende mas sobre ingenieria de menu</Link>.
                </p>
              </div>

              <div className="border-l-4 border-amber-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. Happy hour solo con cocteles de bajo food cost</h3>
                <p className="text-slate-700 leading-relaxed">
                  Si ofreces happy hour, selecciona cocteles con food cost inferior al 18%: mojito, daikiri, espresso martini, mocktails. Un mojito a 6,50 euros en happy hour (vs 9,50 euros normal) aun tiene un food cost del 22,6% — rentable. Un gin tonic premium a 9 euros en happy hour tiene un food cost del 42,8% — estas regalando dinero.
                </p>
              </div>
            </div>

            {/* Section 11: Software vs Excel */}
            <h2 id="software-vs-excel" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Software vs Excel para Bares de Cocteles</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Muchos bartenders y propietarios de bares gestionan sus escandallos en una hoja de calculo o directamente en papel. Funciona cuando tienes 10 cocteles en carta, pero se vuelve inmanejable cuando la carta crece, los precios cambian y necesitas actualizar todo en tiempo real.
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Aspecto</th>
                    <th className="p-3 text-center font-medium">Excel / Hoja de calculo</th>
                    <th className="p-3 text-center font-medium">Software (PLEKO)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Actualizacion de precios', 'Manual, cada vez que cambia un precio', 'Automatica al escanear albaran con OCR'],
                    ['Calculo de mermas', 'Formulas manuales, propenso a errores', 'Mermas predefinidas + personalizables'],
                    ['Coste en cascada (siropes, infusiones)', 'Muy complejo de mantener', 'Sub-recetas automaticas'],
                    ['Alerta de food cost alto', 'No existe — te enteras al cerrar el mes', 'Alertas en tiempo real (semaforo)'],
                    ['Inventario de barra', 'Conteo manual en papel', 'Integrado con stock y ventas'],
                    ['Escalado (eventos, catering)', 'Recalcular todo manualmente', 'Escalador de raciones automatico'],
                    ['Acceso del equipo', 'Archivo compartido (versiones en conflicto)', 'Multiusuario con roles'],
                    ['Coste', 'Gratis (pero requiere tu tiempo)', 'Desde 49 euros/mes (ahorra horas)'],
                  ].map(([aspecto, excel, software], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{aspecto}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600 text-sm">{excel}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-amber-700 text-sm font-medium">{software}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Si tu bar tiene menos de 10 cocteles y los precios cambian poco, una <Link to="/plantilla-escandallo-excel" className="text-amber-600 hover:underline">plantilla de Excel</Link> puede funcionar. Pero si gestionas una carta de 20+ cocteles con ingredientes que cambian de precio cada mes, un <Link to="/software-escandallo" className="text-amber-600 hover:underline">software de escandallos</Link> como PLEKO te ahorra horas de trabajo y evita errores que cuestan dinero.
            </p>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre escandallos de cocteleria</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-amber-500" /> Guias relacionadas</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Escandallo en hosteleria: guia completa' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas: cocteles, vinos y cafes' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/software-escandallo', label: 'Software de escandallo para restaurantes' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: tabla completa' },
                { to: '/margen-beneficio-restaurante', label: 'Margen de beneficio en restaurantes' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-8 text-center text-white">
            <Martini className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Calcula el coste de cada coctel automaticamente</h2>
            <p className="text-amber-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula el escandallo de todos tus cocteles, controla mermas, escanea facturas de distribuidores con IA y te avisa cuando el food cost se dispara. Deja de perder dinero en la barra. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-amber-600 font-bold rounded-xl hover:bg-amber-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-amber-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
